import React, { useState, useEffect } from 'react';
import { 
  Bot, 
  X, 
  Send, 
  Lightbulb, 
  TrendingUp, 
  Calculator,
  FileText,
  ChevronRight,
  Sparkles
} from 'lucide-react';
import { cn } from '@plvplus/core';
import { useUserRoles } from '../../hooks/useUserRoles';
import { useLocation } from 'react-router-dom';

interface AiSuggestion {
  id: string;
  type: 'tip' | 'workflow' | 'calculation' | 'insight';
  title: string;
  description: string;
  action?: {
    label: string;
    href?: string;
    onClick?: () => void;
  };
  priority: 'low' | 'medium' | 'high';
}

interface AppAiAssistantPanelProps {
  isOpen: boolean;
  onClose: () => void;
}

export const AppAiAssistantPanel: React.FC<AppAiAssistantPanelProps> = ({ isOpen, onClose }) => {
  const [message, setMessage] = useState('');
  const [suggestions, setSuggestions] = useState<AiSuggestion[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const { user } = useUserRoles();
  const location = useLocation();

  // Generate contextual suggestions based on current page and user role
  useEffect(() => {
    const generateSuggestions = () => {
      const baseSuggestions: AiSuggestion[] = [];

      // Dashboard suggestions
      if (location.pathname === '/dashboard') {
        baseSuggestions.push({
          id: 'dashboard-tip',
          type: 'tip',
          title: 'Optimisez votre tableau de bord',
          description: 'Personnalisez vos widgets pour afficher les KPIs les plus importants pour votre rôle.',
          action: {
            label: 'Personnaliser',
            href: '/settings/dashboard'
          },
          priority: 'medium'
        });
      }

      // PRI+ specific suggestions
      if (location.pathname.startsWith('/pri-plus')) {
        baseSuggestions.push({
          id: 'pricing-workflow',
          type: 'workflow',
          title: 'Nouveau calcul de prix',
          description: 'Créez un calcul de prix de revient en utilisant nos modèles prédéfinis pour gagner du temps.',
          action: {
            label: 'Créer un calcul',
            href: '/pri-plus/calculations/new'
          },
          priority: 'high'
        });

        baseSuggestions.push({
          id: 'margin-insight',
          type: 'insight',
          title: 'Analyse des marges',
          description: 'Vos marges moyennes sont de 28%. Considérez augmenter à 30% pour améliorer la rentabilité.',
          priority: 'medium'
        });
      }

      // Role-based suggestions
      if (user?.roles.some(role => role.level === 'manager')) {
        baseSuggestions.push({
          id: 'team-performance',
          type: 'insight',
          title: 'Performance équipe',
          description: 'Votre équipe a traité 15% de devis en plus ce mois. Excellent travail !',
          priority: 'low'
        });
      }

      // General productivity tips
      baseSuggestions.push({
        id: 'keyboard-shortcuts',
        type: 'tip',
        title: 'Raccourcis clavier',
        description: 'Utilisez Ctrl+K pour ouvrir la recherche rapide dans n\'importe quel module.',
        priority: 'low'
      });

      setSuggestions(baseSuggestions);
    };

    generateSuggestions();
  }, [location.pathname, user]);

  const handleSendMessage = async () => {
    if (!message.trim()) return;

    setIsLoading(true);
    // Simulate AI response
    setTimeout(() => {
      setMessage('');
      setIsLoading(false);
      // Add AI response logic here
    }, 1000);
  };

  const getSuggestionIcon = (type: string) => {
    switch (type) {
      case 'tip': return Lightbulb;
      case 'workflow': return ChevronRight;
      case 'calculation': return Calculator;
      case 'insight': return TrendingUp;
      default: return Sparkles;
    }
  };

  const getPriorityColor = (priority: string) => {
    switch (priority) {
      case 'high': return 'border-l-red-500 bg-red-50 dark:bg-red-900/10';
      case 'medium': return 'border-l-yellow-500 bg-yellow-50 dark:bg-yellow-900/10';
      case 'low': return 'border-l-blue-500 bg-blue-50 dark:bg-blue-900/10';
      default: return 'border-l-gray-500 bg-gray-50 dark:bg-gray-900/10';
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 overflow-hidden">
      {/* Backdrop */}
      <div className="absolute inset-0 bg-black bg-opacity-50" onClick={onClose} />
      
      {/* Panel */}
      <div className="absolute right-0 top-0 h-full w-full max-w-md bg-white dark:bg-gray-800 shadow-xl">
        <div className="flex h-full flex-col">
          {/* Header */}
          <div className="flex items-center justify-between p-4 border-b border-gray-200 dark:border-gray-700">
            <div className="flex items-center space-x-2">
              <div className="w-8 h-8 bg-gradient-to-br from-purple-500 to-blue-600 rounded-lg flex items-center justify-center">
                <Bot className="h-5 w-5 text-white" />
              </div>
              <div>
                <h2 className="text-lg font-semibold text-gray-900 dark:text-white">
                  Assistant IA
                </h2>
                <p className="text-xs text-gray-500 dark:text-gray-400">
                  Votre aide intelligente PLV+
                </p>
              </div>
            </div>
            <button
              onClick={onClose}
              className="p-2 rounded-md hover:bg-gray-100 dark:hover:bg-gray-700"
            >
              <X className="h-5 w-5" />
            </button>
          </div>

          {/* Content */}
          <div className="flex-1 overflow-y-auto p-4 space-y-4">
            {/* Welcome message */}
            <div className="bg-gradient-to-r from-purple-100 to-blue-100 dark:from-purple-900/20 dark:to-blue-900/20 rounded-lg p-4">
              <h3 className="font-medium text-gray-900 dark:text-white mb-2">
                Bonjour {user?.name.split(' ')[0]} ! 👋
              </h3>
              <p className="text-sm text-gray-600 dark:text-gray-400">
                Je suis là pour vous aider à optimiser votre utilisation de PLV+. 
                Voici quelques suggestions personnalisées :
              </p>
            </div>

            {/* Suggestions */}
            <div className="space-y-3">
              <h4 className="text-sm font-medium text-gray-900 dark:text-white">
                Suggestions intelligentes
              </h4>
              {suggestions.map((suggestion) => {
                const Icon = getSuggestionIcon(suggestion.type);
                return (
                  <div
                    key={suggestion.id}
                    className={cn(
                      'border-l-4 rounded-r-lg p-3 transition-all hover:shadow-sm',
                      getPriorityColor(suggestion.priority)
                    )}
                  >
                    <div className="flex items-start space-x-3">
                      <Icon className="h-5 w-5 text-gray-600 dark:text-gray-400 mt-0.5 flex-shrink-0" />
                      <div className="flex-1 min-w-0">
                        <h5 className="text-sm font-medium text-gray-900 dark:text-white">
                          {suggestion.title}
                        </h5>
                        <p className="text-xs text-gray-600 dark:text-gray-400 mt-1">
                          {suggestion.description}
                        </p>
                        {suggestion.action && (
                          <button
                            onClick={suggestion.action.onClick}
                            className="mt-2 text-xs text-primary-600 dark:text-primary-400 hover:text-primary-700 dark:hover:text-primary-300 font-medium"
                          >
                            {suggestion.action.label} →
                          </button>
                        )}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>

            {/* Quick actions */}
            <div className="space-y-3">
              <h4 className="text-sm font-medium text-gray-900 dark:text-white">
                Actions rapides
              </h4>
              <div className="grid grid-cols-2 gap-2">
                <button className="p-3 border border-gray-200 dark:border-gray-700 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700/50 text-left">
                  <Calculator className="h-5 w-5 text-primary-600 dark:text-primary-400 mb-2" />
                  <p className="text-xs font-medium text-gray-900 dark:text-white">
                    Nouveau calcul
                  </p>
                </button>
                <button className="p-3 border border-gray-200 dark:border-gray-700 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700/50 text-left">
                  <FileText className="h-5 w-5 text-primary-600 dark:text-primary-400 mb-2" />
                  <p className="text-xs font-medium text-gray-900 dark:text-white">
                    Créer devis
                  </p>
                </button>
              </div>
            </div>
          </div>

          {/* Chat input */}
          <div className="border-t border-gray-200 dark:border-gray-700 p-4">
            <div className="flex space-x-2">
              <input
                type="text"
                value={message}
                onChange={(e) => setMessage(e.target.value)}
                onKeyPress={(e) => e.key === 'Enter' && handleSendMessage()}
                placeholder="Posez-moi une question..."
                className="flex-1 px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-white placeholder-gray-500 dark:placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent text-sm"
                disabled={isLoading}
              />
              <button
                onClick={handleSendMessage}
                disabled={isLoading || !message.trim()}
                className="px-3 py-2 bg-primary-600 text-white rounded-md hover:bg-primary-700 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <Send className="h-4 w-4" />
              </button>
            </div>
            <p className="text-xs text-gray-500 dark:text-gray-400 mt-2">
              L'IA peut faire des erreurs. Vérifiez les informations importantes.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};
